import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import { getRouteStackPath } from '@portal/config/routes';
import { translate } from '@portal/services/i18n';
import React, { useState } from 'react';
import './Limits.less';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import { formatCurrency, maskMoney, unmaskField } from '@portal/services/masks';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import { Input, Tabs, Steps } from 'antd';
import useReduxState from '@portal/hooks/useReduxState';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import { useNavigation } from '@portal/hooks/useNavigation';

enum StepIndexEnum {
  STEP_ONE = 1,
  STEP_TWO = 2,
  STEP_THREE = 3,
  STEP_FOUR = 4,
}

enum TabIndexEnum {
  SOLICITATION = 1,
  HISTORY = 2,
}

const Limits = () => {
  const navigate = useNavigation();
  const {
    products: { limitSolicitations, escrowAccount },
  } = useReduxState();

  const [tabIndex, setTabIndex] = useState<TabIndexEnum>(
    TabIndexEnum.SOLICITATION
  );
  const [newLimit, setNewLimit] = useState('0');
  const [reason, setReason] = useState('');

  const steps = () => {
    const stepsArray = [
      {
        value: StepIndexEnum.STEP_ONE,
        title: translate('PAGES.PRODUCTS.ESCROW.LIMITS.STEPS.STEP_ONE'),
      },
      {
        value: StepIndexEnum.STEP_TWO,
        title: translate('PAGES.PRODUCTS.ESCROW.LIMITS.STEPS.STEP_TWO'),
      },
      {
        value: StepIndexEnum.STEP_THREE,
        title: translate('PAGES.PRODUCTS.ESCROW.LIMITS.STEPS.STEP_THREE'),
      },
      {
        value: StepIndexEnum.STEP_FOUR,
        title: translate('PAGES.PRODUCTS.ESCROW.LIMITS.STEPS.STEP_FOUR'),
      },
    ];

    return (
      <div className="limits-escrow__steps">
        <Steps items={stepsArray} />
      </div>
    );
  };

  const renderLimitsDetails = () => {
    const limits = [
      {
        title: translate('PAGES.PRODUCTS.ESCROW.LIMITS.LIMIT_USED'),
        value: formatCurrency(escrowAccount?.used || 0),
      },
      {
        title: translate('PAGES.PRODUCTS.ESCROW.LIMITS.AVAILABLE_LIMIT'),
        value: formatCurrency(escrowAccount?.available || 0),
      },
    ];

    return limits.map((limit) => (
      <div
        key={limit.title}
        className="limits-escrow__container__card__limits-detail__row"
      >
        <p>{limit.title}</p>
        <p>{limit.value}</p>
      </div>
    ));
  };

  const renderSolicitations = () => {
    return (
      <div>
        {limitSolicitations?.map((item) => {
          const solicitation = [
            {
              label: translate('PAGES.PRODUCTS.ESCROW.LIMITS.COLUMNS.REASON'),
              value: item.reason,
            },
            {
              label: translate('PAGES.PRODUCTS.ESCROW.LIMITS.COLUMNS.AMOUNT'),
              value: formatCurrency(Number(item.value)),
            },
            {
              label: translate('PAGES.PRODUCTS.ESCROW.LIMITS.COLUMNS.DATE'),
              value: item.date,
            },
            {
              label: translate('PAGES.PRODUCTS.ESCROW.LIMITS.COLUMNS.STATUS'),
              value: item.statusLabel,
            },
          ];

          return (
            <div
              className="limits-escrow__history__items fade-in"
              key={item.id}
            >
              <div className="limits-escrow__history__items__header">
                <h4>{item.id}</h4>
              </div>
              <div className="limits-escrow__history__items__body">
                {solicitation.map((field) => (
                  <div key={field.label}>
                    <p className="limits-escrow__history__items__label">
                      {field.label}
                    </p>
                    <p className="limits-escrow__history__items__value">
                      {field.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const tabOptions = [
    {
      label: translate('PAGES.PRODUCTS.ESCROW.LIMITS.TABS.SOLICITATION'),
      value: TabIndexEnum.SOLICITATION,
      children: (
        <>
          {steps()}
          <div className="limits-escrow__container">
            <div className="limits-escrow__container__card">
              <div className="limits-escrow__container__card__header">
                <p className="limits-escrow__container__card__header__title">
                  {translate('PAGES.PRODUCTS.ESCROW.LIMITS.REQUEST_DATA.TITLE')}
                </p>
              </div>
              <AdvancedInput
                label={translate(
                  'PAGES.PRODUCTS.ESCROW.LIMITS.REQUEST_DATA.AMOUNT'
                )}
                value={maskMoney(newLimit)}
                onChange={(value) => {
                  const numericValue = unmaskField(value);
                  setNewLimit(numericValue);
                }}
              />
              <div className="limits-escrow__container__text-area">
                <Input.TextArea
                  placeholder={translate(
                    'PAGES.PRODUCTS.ESCROW.LIMITS.REQUEST_DATA.REASON'
                  )}
                  rows={8}
                  autoFocus
                  value={reason}
                  onChange={(element) => setReason(element.target.value)}
                />
              </div>
            </div>
            <div className="limits-escrow__container__card">
              <div className="limits-escrow__container__card__header">
                <p className="limits-escrow__container__card__header__title">
                  {translate(
                    'PAGES.PRODUCTS.ESCROW.LIMITS.REQUEST_DATA.CURRENT_LIMIT'
                  )}
                </p>
              </div>
              <div>
                <p className="limits-escrow__container__card__current-limit">
                  {formatCurrency(100000)}
                </p>
                <div className="limits-escrow__container__card__limits-detail">
                  {renderLimitsDetails()}
                </div>
              </div>
              <div className="limits-escrow__container__card__limits-detail__button">
                <AdvancedButton
                  text={translate(
                    'PAGES.PRODUCTS.ESCROW.LIMITS.REQUEST_DATA.BUTTONS.REQUEST_LIMIT'
                  )}
                  variant="contained"
                  onClick={() => setTabIndex(TabIndexEnum.HISTORY)}
                />
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      label: translate('PAGES.PRODUCTS.ESCROW.LIMITS.TABS.HISTORY'),
      value: TabIndexEnum.HISTORY,
      children: (
        <div className="limits-escrow__history">{renderSolicitations()}</div>
      ),
    },
  ];

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate('COMPONENTS.BREADCRUMB.PRODUCTS'),
          },
          {
            active: true,
            title: translate('PAGES.PRODUCTS.ESCROW.TITLE'),
            url: getRouteStackPath('PRODUCTS', 'PRODUCT_ESCROW'),
          },
          {
            active: false,
            title: translate('PAGES.PRODUCTS.ESCROW.LIMITS.TITLE'),
          },
        ]}
      />
      <div className="limits-escrow">
        <AdvancedPanelContentHeader
          testId="limits-escrow-header-card"
          title={translate('PAGES.PRODUCTS.ESCROW.LIMITS.TITLE')}
          description={translate('PAGES.PRODUCTS.ESCROW.LIMITS.DESCRIPTION')}
          goBackAction={() =>
            navigate(getRouteStackPath('PRODUCTS', 'PRODUCT_ESCROW'))
          }
        />
        <AdvancedCard testId="limits-escrow-tabs-card">
          <Tabs
            activeKey={String(tabIndex)}
            onChange={(key) => setTabIndex(Number(key))}
            type="card"
          >
            {tabOptions.map((tab) => (
              <Tabs.TabPane tab={tab.label} key={tab.value}>
                {tab.children}
              </Tabs.TabPane>
            ))}
          </Tabs>
        </AdvancedCard>
      </div>
    </>
  );
};

export default Limits;
